import React, { useState } from 'react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { observer } from 'mobx-react';
import styled from 'styled-components/macro';

import { useDataStore } from 'state/DataStore';
import joinPath from 'utilities/joinPath';

import Page from 'components/Page';
import UnitSelector from 'components/UnitSelector';


const Level = observer(() => {
    const [ selectedCategory, setSelectedCategory ] = useState(null);
    const { levelSlug } = useParams();
    const match = useRouteMatch();
    const history = useHistory();
    const dataStore = useDataStore();
    
    const level = dataStore.getItem('levels', 'slug', levelSlug);
    
    if (!level) {
        return (
            <Page>
                <h1>Level Not Found</h1>
            </Page>
        );
    }
    
    const { units, unit_types, unit_categories } = dataStore.data;
    const levelUnits = units.filter(unit => unit.acf.level === level.id);

    const availableUnits = levelUnits.filter(unit => unit.acf?.status === 'available');
    
    const currentCategoryIDs = [...new Set(availableUnits.flatMap(unit => {
        const unitType = unit_types.find(type => type.id === unit.acf.unit_type);

        return unitType.unit_categories;
    }))];

    const currentCategories = unit_categories.filter(category => currentCategoryIDs.includes(category.id));

    const handleUnitSelect = (unitSlug) => {
        history.push(joinPath(match.url, unitSlug));
    };
    
    return (
        <Page>
            <LevelPlan
                level={level}
                units={levelUnits}
                categories={currentCategories}
                onSelect={handleUnitSelect}
                $currentCategory={selectedCategory}
            />

            <Legend className="h6">
                <LegendButtons>
                    {currentCategories.map(category => (
                        <LegendItem key={category.slug} onClick={() => setSelectedCategory(selectedCategory === category.slug ? null : category.slug)} >
                            <LegendItemIcon color={category.acf.color} />
                            <LegendItemLabel>{category.name}</LegendItemLabel>
                        </LegendItem>
                    ))}
                </LegendButtons>

                <LegendItem key="sold">
                    <LegendItemIcon color="#DE8686" />
                    <LegendItemLabel>Sold</LegendItemLabel>
                </LegendItem>
            </Legend>
        </Page>
    );
});


export default Level;


const LevelPlan = styled(UnitSelector)`
    width: 1300px;
    max-width: 100%;
    height: 100%;

    .area {
        opacity: 0;
        transition: all .3s;
    }
    .area *:is(.area-outline, .area_outline) {
        transition: all .3s;
    }

    .area[data-status="sold"] {
        pointer-events: none;
        opacity: 1;
    }
    .area[data-status="sold"] *:is(.area-outline, .area_outline) {
        fill: ${props => props.theme.sold_color};
        opacity: 1;
    }
    
    ${props => props.categories && props.categories.length > 0 && props.categories.map(category => `
        .area[data-category="${category.slug}"]:not([data-status="sold"]) *:is(.area-outline, .area_outline) {
            fill: ${category.acf.color};
            opacity: 1;
        }
    `).join('')}

    ${props => props.$currentCategory && `
        .area[data-category="${props.$currentCategory}"] {
            opacity: 1;
        }
    `}
    
    .area:not([data-status="sold"]):hover {
        opacity: 1;
    }
    .area:not([data-status="sold"]):hover *:is(.area-outline, .area_outline) {
        fill: ${props => props.theme.primary_color};
        opacity: .6;
    }
`;


const Legend = styled.div`
    position: absolute;
    bottom: 3rem;
    right: 3rem;
    z-index: 10;
    display: flex;
    flex-direction: column;
    padding: 3rem;
    gap: 1.5rem;
    border: 1px solid #BFBFBF;
    width: 25rem;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 1.5rem;
`;

const LegendItemIcon = styled.div`
    flex: 0 0 auto;
    width: 2.5rem;
    height: 2.5rem;
    background-color: ${props => props.color};
`;

const LegendItemLabel = styled.div`
    flex: 1 1 auto;
`;


const LegendButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    border-bottom: 1px solid #D8D8D8;
    padding-bottom: 1.5rem;

    ${LegendItem} {
        cursor: pointer;
    }
`;





